import { useRef, useState } from 'react';
import type { Garment, GarmentCategory, GarmentViewKind, GhostSide } from '@studio/shared';

import { api } from './api';
import { downscaleImage } from './downscale';
import { inspectImage, type CaptureIssue } from './capture';
import { useT } from './i18n';
import { looksMachineMade } from './names';
import { useDialog } from '../components/Dialog';

/** Details are the one view a piece can have several of: the zip, a cuff, the print. */
export const MAX_DETAILS = 6;

/*
 * Garment photographs keep more of their pixels than anything else. A
 * button, a stitch or the scale of a print is a few pixels on a full-length
 * photo, and the renderer cannot copy a detail it was never sent.
 */
const GARMENT_EDGE = 2400;

export const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];

/** Default layer per category: higher sits further from the skin. */
export const DEFAULT_LAYER: Record<GarmentCategory, number> = {
  top: 5,
  outerwear: 10,
  bottom: 4,
  dress: 6,
  shoes: 1,
  bag: 12,
  accessory: 11,
  headwear: 11,
};

export const CATEGORIES: GarmentCategory[] = [
  'top',
  'outerwear',
  'bottom',
  'dress',
  'shoes',
  'bag',
  'accessory',
  'headwear',
];

/** Guesses a category from the filename so the common case needs no edit. */
function guessCategory(filename: string): GarmentCategory {
  const name = filename.toLowerCase();
  const rules: Array<[RegExp, GarmentCategory]> = [
    [/jean|trouser|pant|short|skirt|denim/, 'bottom'],
    [/vest|jacket|coat|blazer|cardigan|gilet/, 'outerwear'],
    [/dress|gown/, 'dress'],
    [/shoe|boot|heel|sneaker|sandal/, 'shoes'],
    [/bag|purse|tote|clutch/, 'bag'],
    [/hat|cap|beanie|scarf/, 'headwear'],
    [/earring|necklace|bracelet|ring|belt/, 'accessory'],
  ];
  return rules.find(([pattern]) => pattern.test(name))?.[1] ?? 'top';
}

/** Whether a piece has detail worth a close photograph it does not have yet. */
export function wantsDetailPhoto(garment: Garment): boolean {
  const a = garment.analysis;
  if (!a || (garment.views ?? []).some((v) => v.kind === 'detail')) return false;
  const text = `${a.patterns} ${a.texture} ${a.hardware} ${(a.construction ?? []).join(' ')}`;
  return (
    /embroider|brod|print|motif|sequin|bead|jacquard|lace|plaid|check|tartan|stripe|houndstooth|tweed|boucl/i.test(
      text,
    ) || (a.countables?.length ?? 0) >= 3
  );
}

/**
 * Everything that can be done to the clothes of a roll, whatever the page
 * looks like: upload a piece, wear one already on file, add the other sides
 * of it, name it, take it off. The layouts that use it only decide where the
 * buttons go.
 */
export function useGarments(garments: Garment[], onChange: (garments: Garment[]) => void) {
  const t = useT();
  const { ask } = useDialog();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /*
   * The wardrobe: everything this account has ever photographed. Loaded when
   * it is asked for rather than on mount — most rolls are new clothes, and
   * a list nobody opened is a request nobody needed.
   */
  const [wardrobe, setWardrobe] = useState<Garment[] | undefined>();
  /*
   * What the light meter thought of the last batch. A warning, never a block:
   * one blurred photograph is read once by the analyst and then reproduced,
   * wrongly, in every frame of every roll that wears the piece — but the
   * person holding the camera can see things the arithmetic cannot.
   */
  const [notes, setNotes] = useState<Array<{ name: string; issues: CaptureIssue[] }>>([]);

  const openWardrobe = async () => {
    if (wardrobe) return;
    try {
      setWardrobe(await api.listGarments());
    } catch {
      setWardrobe([]);
    }
  };

  /** Pressing a piece puts it on the roll; pressing it again takes it off. */
  const wear = (garment: Garment) => {
    if (garments.some((g) => g.id === garment.id)) {
      onChange(garments.filter((g) => g.id !== garment.id));
      return;
    }
    onChange([...garments, garment]);
  };

  /** Removes a piece from the wardrobe for good, not just from this roll. */
  const destroy = async (garment: Garment) => {
    const yes = await ask({
      title: t('wardrobe.confirmDeleteTitle', { label: garment.label }),
      body: t('wardrobe.confirmDelete', { label: garment.label }),
      confirmLabel: t('common.deleteIt'),
      danger: true,
    });
    if (!yes) return;
    setWardrobe((prev) => prev?.filter((g) => g.id !== garment.id));
    onChange(garments.filter((g) => g.id !== garment.id));
    await api.deleteGarment(garment.id).catch(() => undefined);
  };

  /**
   * Each photograph a new piece, from the front. Its kind is the one chosen,
   * or guessed from the filename when none was. Returns the pieces made.
   */
  const upload = async (files: FileList | File[] | null, kind?: GarmentCategory): Promise<Garment[]> => {
    const images = Array.from(files ?? []).filter((file) => ACCEPTED.includes(file.type));
    if (!images.length) {
      if (files && Array.from(files).length) setError(t('wardrobe.notAnImage'));
      return [];
    }
    setBusy(true);
    setError('');

    const added: Garment[] = [];
    const looked: Array<{ name: string; issues: CaptureIssue[] }> = [];
    for (const file of images) {
      try {
        const reading = await inspectImage(file);
        if (reading?.issues.length) looked.push({ name: file.name, issues: reading.issues });

        const asset = await api.uploadAsset(await downscaleImage(file, GARMENT_EDGE), 'garment');
        const category = kind ?? guessCategory(file.name);
        /*
         * The filename is a good first name for a piece when a person chose
         * it ("chalk-blazer.jpg") and a meaningless one when a phone did
         * ("71FAD406-4672-….jpeg"). The second kind gets the category's name
         * instead — a word the studio can read, and that anyone can change.
         */
        const fromFile = file.name
          .replace(/\.[^.]+$/, '')
          .replace(/[-_]+/g, ' ')
          .trim();
        const created = await api.createGarment({
          assetId: asset.id,
          label: looksMachineMade(fromFile) ? t(`enum.category.${category}`) : fromFile,
          category,
          layer: DEFAULT_LAYER[category],
        });
        added.push(created);
        setWardrobe((prev) => (prev ? [created, ...prev] : prev));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('caster.errUpload'));
      }
    }

    onChange([...garments, ...added]);
    setNotes(looked);
    setBusy(false);
    return added;
  };

  const patch = async (id: string, changes: Partial<Garment>) => {
    // Update locally first so typing in the label field stays responsive.
    onChange(garments.map((g) => (g.id === id ? { ...g, ...changes } : g)));
    try {
      await api.updateGarment(id, changes);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('wardrobe.saveFailed'));
    }
  };

  /** Another side of a piece. Details can come several at a time, up to the limit. */
  const addView = async (
    id: string,
    kind: GarmentViewKind,
    files: FileList | File[] | null,
    side?: 'left' | 'right',
  ) => {
    const garment = garments.find((g) => g.id === id);
    const room =
      kind === 'detail' ? MAX_DETAILS - (garment?.views ?? []).filter((v) => v.kind === 'detail').length : 1;
    const picked = Array.from(files ?? [])
      .filter((file) => ACCEPTED.includes(file.type))
      .slice(0, Math.max(0, room));
    if (!picked.length) {
      if (files && Array.from(files).length && room > 0) setError(t('wardrobe.notAnImage'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      let updated: Garment | undefined;
      for (const file of picked) {
        const asset = await api.uploadAsset(await downscaleImage(file, GARMENT_EDGE), 'garment');
        updated = await api.addGarmentView(id, asset.id, kind, side);
      }
      if (updated) {
        const next = updated;
        onChange(garments.map((g) => (g.id === id ? next : g)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('caster.errUpload'));
    }
    setBusy(false);
  };

  /*
   * The piece's ghost photographs: one per side, each made in the background
   * as soon as that side's photograph is in, and tracked as `piece:side` so
   * the guide can show which are still being made while it moves on.
   */
  const [ghosting, setGhosting] = useState<string[]>([]);
  const inFlight = useRef(new Set<string>());
  const latest = useRef(garments);
  latest.current = garments;
  const makeGhosts = async (id: string, sides: GhostSide[], retouch?: { correction?: string }) => {
    const keys = sides.map((side) => `${id}:${side}`).filter((key) => !inFlight.current.has(key));
    if (!keys.length) return;
    for (const key of keys) inFlight.current.add(key);
    setGhosting([...inFlight.current]);
    try {
      const updated = await api.makeGarmentGhosts(
        id,
        keys.map((key) => key.split(':')[1] as GhostSide),
        retouch,
      );
      // The look as it is now, not as it was when this was asked for: pieces may have come and gone since.
      onChange(latest.current.map((g) => (g.id === id ? { ...g, ghosts: updated.ghosts } : g)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dress.ghostFailed'));
    }
    for (const key of keys) inFlight.current.delete(key);
    setGhosting([...inFlight.current]);
  };

  const noteView = async (id: string, assetId: string, note: string) => {
    try {
      const updated = await api.noteGarmentView(id, assetId, note);
      onChange(garments.map((g) => (g.id === id ? updated : g)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('wardrobe.saveFailed'));
    }
  };

  const removeView = async (id: string, assetId: string) => {
    try {
      const updated = await api.removeGarmentView(id, assetId);
      onChange(garments.map((g) => (g.id === id ? updated : g)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('wardrobe.saveFailed'));
    }
  };

  /*
   * Out of this roll, not out of the wardrobe. A piece photographed once is
   * worth keeping: the analysis that was paid for is stored on it, and the
   * next roll that wears it pays nothing.
   */
  const remove = (id: string) => {
    onChange(garments.filter((g) => g.id !== id));
  };

  return {
    busy,
    error,
    notes,
    clearNotes: () => setNotes([]),
    wardrobe,
    openWardrobe,
    wear,
    destroy,
    upload,
    patch,
    addView,
    ghosting,
    makeGhosts,
    noteView,
    removeView,
    remove,
  };
}
